import type {
  CategoryForBet,
  DetailedTypeOfBet,
  FantasyAnalyticsEventArgs,
  FantasyAnalyticsEventProperties,
  FantasyAnalyticsEvents,
  FantasyExperience,
  GeneralTypeOfBet,
  PFFLetterGrade,
  User,
  SmartBetsTab,
  ConnectScreenInfo,
  ConnectUserGroupInfo,
  ConnectScreens,
  ConnectAnalyticsEventArgs,
  ConnectAnalyticsEventProperties,
  ConnectAnalyticsEvents,
  GameSimulatorTabs,
  GameSimulatorAnalyticsEventArgs,
  GameSimulatorAnalyticsEventProperties,
  GameSimulatorAnalyticsEvents,
  LeagueInfoForAnalytics,
  FantasyCtaInfo,
  PlayerProfileWidgetNames,
  PerformanceMetricStat,
  IdpRankingsExperiences,
  FantasyOnboarding,
  FootballPlayer,
  WeekGroupNfl,
  WeekGroupNcaa,
  PlayerProfileLocation,
  PlayerProfileScreenNames,
  PlayerProfileLinkNames,
  AccountPages,
  BettingScreens,
  BettingTabClickedEventProperties,
  BettingTabs,
} from '@pff-consumer/schema'
import { Platform } from 'react-native'
import { NavigationContainerProps } from '@react-navigation/native'
import { LinkingOptions } from '@react-navigation/native/lib/typescript/src/types'

export type PlayerGradeAnalysisEvents = 'Past Defense Tab Clicked' | 'Swiped to Last 5 Games View'

export enum EventNames {
  TAB_CLICKED = 'Tab Clicked',
}

export type EventName =
  | 'Account Created'
  | 'Application Backgrounded'
  | 'Application Installed'
  | 'Application Opened'
  | 'Article Bookmarked'
  | 'Article Opened'
  | 'Article Read'
  | 'Article Share Started'
  | 'Article Shared'
  | 'Best Bets View Loaded'
  | 'Bet Cart Bet Removed'
  | 'Bet Cart Clear All'
  | 'Bet Cart Closed'
  | 'Bet Cart Opened'
  | 'Bet Cart Place Bet'
  | 'Bet Deselected'
  | 'Bet Selected'
  | 'Bookmark Removed'
  | 'CTA Clicked'
  | 'Element Clicked'
  | 'Filter Applied'
  | 'Game Card Analysis Collapsed'
  | 'Game Card Analysis Expanded'
  | 'Game Card Analysis Viewed'
  | 'Game Card More Analysis Modal Closed'
  | 'Game Card More Analysis Modal Opened'
  | 'Games View Loaded'
  | 'Logged In'
  | 'League Added'
  | 'League Removed'
  | 'League Sync Error'
  | 'Remove League Error'
  | 'Player Props View Loaded'
  | 'Key Insights View Loaded'
  | 'Screen Load'
  | 'ScrollToTopButton Clicked'
  | 'Subscription Plan Selected'
  | 'Subscription Purchased'
  | 'Welcome Modal Closed'
  | 'Welcome Modal Get Started Clicked'
  | 'Welcome Modal Screen Viewed'
  | 'Buy Insight Viewed'
  | 'Buy Insight Clicked'
  | 'Sell Insight Viewed'
  | 'Sell Insight Clicked'
  | FantasyAnalyticsEvents
  | ConnectAnalyticsEvents
  | GameSimulatorAnalyticsEvents
  | 'Survey Answered'
  | 'Game Selected'
  | 'Experiment Viewed'
  | 'Card Clicked'
  | 'MDS Shared'
  | 'MDS Share Started'
  | PlayerGradeAnalysisEvents
  | 'Subscribe Flow Conversion'
  | 'Transaction Canceled'
  | 'Transaction Failed'
  | 'App-wide Error'
  | 'Upsell Modal Viewed'
  | 'Card Swiped'
  | 'SPC Default Monthly Plan'
  | 'SPC Default Annual Plan'
  | 'SPC Monthly Plan Variant'
  | 'Start Sit Optimizer Toggle'
  | 'League Sync Filter Opened'
  | 'League Sync Filter Selected'
  | 'Turned On Push Notifications'
  | 'Turned Off Push Notifications'
  | 'Matchup Selected'
  | 'Cancel Survey Answered'
  | 'Subscription Canceled'
  | 'Subscribe Hero Image Viewed'
  | 'Subscribe Experiment Viewed'
  | 'Player Profile Player Card Clicked'
  | 'Player Profiles'
  | 'Field Changed'
  | EventNames
  | 'Search Usage'
  | 'ID.me Verified'
  | 'Dropdown Clicked'
  | 'Checkbox Selected'
  | 'Slider Adjusted'
  | 'Email Opt-In'

export type ScreenName =
  | 'Account'
  | 'My Account'
  | 'Create Account'
  | 'Delete Account Modal'
  | 'Delete Account Screen'
  | 'Delete Account'
  | 'Fantasy'
  | 'Feed'
  | 'Locked Article'
  | 'Log In'
  | 'My PFF'
  | 'Reset Password'
  | BettingScreens
  | BettingTabs
  | 'Subscribe Now'
  | 'Subscription Plan Selection'
  | 'Webview'
  | 'Reading List'
  | 'OnboardingQuestion-1'
  | 'Mismatch explainer modal'
  | 'In Game Experience'
  | ConnectScreens
  | GameSimulatorTabs
  | 'Smart Bets Subscribe Now'
  | 'Smart Bets - Key Insights'
  | 'Start Sit Subscribe Now'
  | 'Feed Best Bets'
  | 'NFL Draft'
  | 'Set Password'
  | 'Upsell Modal - Variant 1' // legacy, combines location and variant, avoid using
  | 'MDS Upsell Modal'
  | 'League Sync - Create Account'
  | 'League Sync - Step 1'
  | 'League Sync - Step 2'
  | 'League Sync - Step 3'
  | 'League Sync - My Leagues'
  | 'Fantasy Mock Draft Simulator Draft'
  | 'My Team Dashboard'
  | 'Opt-In Screen'
  | 'Cancel Subscription'
  | 'Cancel - Info Screen'
  | 'Cancel - Confirmation Modal'
  | 'Cancel - Confirmation Screen'
  | 'Cancel - Error Screen'
  | 'Player Profile'
  | 'Player Search'
  | PlayerProfileScreenNames
  | 'Player Props'

export type SubscriptionType = 'Monthly' | 'Annual' | 'Free'
export type SubscriptionStatus = 'Active' | 'Expired' | 'Free' | 'Canceled'
export type PushNotificationExperienceType = 'My Account' | 'Opt-In Screen'
export type ArticleExperience = 'Feed' | 'In-Article' | 'MDS Webview'
export type FantasyLeagueSyncExperience =
  | 'My Team Dashboard'
  | 'Rankings'
  | 'Matchups'
  | 'Waiver Target'
  | 'Start Sit'
  | 'Unknown'
export type AppOpenType = 'Opened' | 'Foregrounded'
export type ArticleLength = 'Half Article' | 'Full Article'
export type IsPremium = true | false
export type SubscriptionLocation =
  | 'New User Sign Up'
  | 'Reactivate'
  | 'Free User Drawer'
  | 'Start Sit'
  | 'Smart Bets'
  | 'for-you'
  | 'Single Page Checkout'
  | FantasyExperience
  | undefined

export type LeagueSyncLocation =
  | 'Create Free Account'
  | 'League Sync Step 1'
  | 'League Sync Step 2'
  | 'League Sync Step 3'
  | 'League Sync Step 4'
  | 'League Sync Step 5'
  | 'My Leagues'
  | 'Delete League Modal'
  | 'League Sync Failed Modal'
  | 'League Sync Filter'
  | 'My Team Dashboard'
  | 'Locked Matchup'
  | 'Fantasy Re-sync League Modal'

export type KeeperOnboardingModalLocation =
  | 'FMDS Onboarding Modal Keepers'
  | 'FMDS Onboarding Modal Grades'
  | 'LDA Onboarding Modal Keepers'
  | 'LDA Onboarding Modal Grades'

export type UpdateExtensionModalLocation =
  | 'FMDS Old Chrome Extension Alert'
  | 'LDA Old Chrome Extension Alert'
  | 'League Sync Old Chrome Extension Alert'

export type MDSLocation = 'Fantasy Mock Draft Sim Upsell'

export type SubscriptionPlanType = 'Monthly' | 'Annual'
export type ArticleType = 'Free' | 'Premium'

export type ArticleCategoryType = 'Fantasy' | 'Betting' | 'Pro Analysis' | 'News'
export type ArticleFilteredViewType = 'All' | 'Fantasy' | 'Betting' | 'Pro Analysis' | 'News'
export type ArticleLocationType = 'Feed' | 'In-Article'

export interface UserInfo {
  pffUserId?: string
  loggedIn: boolean
  subscriptionType?: SubscriptionType
  subscriptionStatus?: SubscriptionStatus
  locationCountry: string | null
  inTrial?: boolean
  articleType?: ArticleType
  articleLocked?: boolean
  firstLaunchTimestamp?: number
  daysSinceInstall?: number
  first7Days?: boolean
  first14Days?: boolean
  first21Days?: boolean
  isNotificationsEnabled?: boolean
}

export interface AppInfo {
  version: string
  mobilePlatform: typeof Platform.OS
}

export interface DeviceInfo {
  device?: string
}

export interface AppOpenedInfo {
  method: AppOpenType
}

interface MyTeamDashboardScreenInfo {
  numLeaguesSynced?: number
  leagueSite?: string
  leagueOptimized?: boolean
}

export interface ScreenInfo extends MyTeamDashboardScreenInfo {
  screenName: ScreenName
  numberArticlesBookmarked?: number
  variant?: boolean | string
  locked?: boolean
  skipNextJsAuth?: boolean
}

export interface WebViewInfo {
  screenName: ScreenName
  url: string
}

export interface MyLeaguesViewInfo {
  screenName: ScreenName
  numberOfFantasyLeagues: number
}

export interface PlayerProfileAppInfo {
  screenName: ScreenName
  leagueName: string
  player_id: number
  player_name: string
}

export interface EventInfo {
  createdTimestamp: number
}

export interface ArticleInfo {
  headline: string
  author: string
  experience: ArticleExperience
  category: string
  filteredView: string
  premium: boolean
  articleType: ArticleType
  articleLocked: boolean
  bookmarked?: boolean
  locationOpened?: string
}
export interface SharedWebViewInfo {
  experience: ArticleExperience
}

export interface FeedEdgeInfo {
  element_name: string
  element_location: string
}

export interface FeedBestBetsInfo {
  experiment: string
  game_week: string
}

export interface ExperimentViewedInfo {
  experiment: string
  variant?: string | boolean
}

export interface ArticleBookmarkInfo {
  category: string
  filteredView: string
  premium: IsPremium
  location: string
  headline: string
  author: string
  article_id: number
}

export interface ArticleReadInfo {
  headline: string
  author: string
  category: string
  filteredView: string
  premium: IsPremium
  length: ArticleLength
  bookmarked?: boolean
  locationOpened?: string
}

export interface ElementInfo {
  device?: string
  elementLocation?: string
  elementName?: string
  hyperlinkPath?: string
  player_name?: string
  skipNextJsAuth?: boolean
  tabName?: string
  ctaName?: string
}

export interface FantasyToolsElementInfo extends ElementInfo {
  tool?: string
  draft_status?: string
  pick_status?: string
  filter_type?: string
  filter_applied?: string
  search_term?: string
  player_id?: number
  suggested_player?: string
  player_position?: string
  draft_round?: number
  overall_pick_number?: number
  player_pick_grade?: number
  toggle_status?: string
}

export interface CtaInfo {
  ctaName: string
  ctaLocation:
    | ScreenName
    | SubscriptionLocation
    | LeagueSyncLocation
    | MDSLocation
    | KeeperOnboardingModalLocation
    | UpdateExtensionModalLocation
    | IdpRankingsExperiences
    | FantasyOnboarding
    | AccountPages
  experience?: FantasyLeagueSyncExperience | ScreenName
  headline?: string
  author?: string
  category?: string
  playerName?: string
  propKey?: string
  skipNextJsAuth?: boolean
  path?: string
  week?: string
  season?: number
  draftRound?: number
  cardClicked?: number
  // shouldn't be boolean, but required with how we merge all of our types
  variant?: string | boolean
  subscriptionPlan?: string
  subscriptionStatus?: SubscriptionStatus
  platformName?: string
  propertyTag?: string
}

export interface FilterAppliedInfo {
  location: string
  filterCategory: string
}

export interface BetSelectedInfo {
  bet_category: CategoryForBet
  general_bet_type?: GeneralTypeOfBet
  detailed_bet_type?: DetailedTypeOfBet
  bet_money_line?: number
  bet_grade?: PFFLetterGrade
  team: string
  game_week: string
  game_time?: number
  season_year: number
  bet_location?: string
  player?: string
}

export interface GameCardAnalysisInfo {
  analysis_type: string
  analysis_type_card_number?: string
  touch_point?: string
  viewed_from?: 'Game Card' | 'More Analysis Modal'
  home_team: string
  away_team: string
  game_week: string
  game_time: number
  season_year: number
}

export interface BetCartAnalysisInfo {
  bet_count: number
  bet_category_count: { [key in CategoryForBet]: number }
  general_bet_type_count: { [key in GeneralTypeOfBet]: number }
  detailed_bet_type_count: { [key in DetailedTypeOfBet]: number }
  game_id_count: { [x: number]: number }
  team_count: { [team: string]: { count: number; home: number; away: number } }
  game_time_map: { [x: number]: number }
  sportsbook?: string
  current_tab: SmartBetsTab
}

export interface WelcomeModalAnalysisInfo {
  welcome_modal_screen_number?: number
}

export interface ScrollToTopButtonAnalysisInfo {
  screen_scroll_button_was_clicked_on: 'Feed' | 'Smart Bets' | 'Fantasy'
}

export interface SubscriptionPurchasedAnalysisInfo {
  previousSubscriptionType: SubscriptionType
  paymentMethod: 'Apple Pay'
  isTrialIncluded: boolean
  paymentFailed: boolean
}

export interface SubscriptionPlanSelectedAnalysisInfo {
  // locationState: string   //TODO Later
  // locationCity: string    //TODO Later
  subscriptionPlanType: SubscriptionPlanType
  experience: SubscriptionLocation
  price: number
  isTrialIncluded: boolean
  skipNextJsAuth?: boolean
}

export interface LogInInfo {
  accountMethod: 'email' | 'apple' | 'google' | 'facebook' | string
}

export interface AccountCreatedInfo {
  accountMethod: 'email' | 'apple' | 'google' | 'facebook' | string
  subscriptionType: SubscriptionType | undefined
  source?: string
}

export interface SurveyAnsweredInfo {
  timeToRespond: number
  questionId: number
  question: string
  answer: string
  answerOrder: number | null
  skipped: boolean
  variant: boolean | string
}

export interface GameSelectedEvent {
  yes_or_no: 'Yes' | 'No'
}

export interface MDSDraftCardInfo {
  cardName: string
  cardLocation: string
}
export interface PlayerGradeAnalysisInfo {
  location: string
  teamId?: number
}

export interface SubscribeFlowConversionInfo {
  key: string
}

export interface TransactionCanceledInfo {
  payment_method: string
  email: string
}

export interface TransactionFailedInfo {
  error_source: string
  error_type: string | undefined
  email: string
}

export interface GlobalErrorInfo {
  email?: string
  error: string
  error_type: string
}

export interface LeagueSyncErrorInfo {
  error: string
  leagueSyncStep?: number
  leagueName?: string
  leagueSite?: string
}

export interface UpsellModalViewInfo {
  location: string
  variant?: boolean | string
}

export interface SPCMonthlyPlanVariantInfo {
  variant: boolean | string
}

export interface CardSwipedInfo {
  location: string
  variant: boolean | string
  cardSwiped?: number
  swipeDirection?: string
}

export interface StartSitOptimizerToggleInfo {
  leagueSite: string
  toggleLocked: boolean
  toggleStatus: 'on' | 'off'
}

export interface LeagueSyncFilterOpenedInfo {
  experience: FantasyLeagueSyncExperience
}

export interface LeagueSyncFilterSelectedInfo {
  experience: FantasyLeagueSyncExperience
  leagueSite: string
}

export interface MatchupPillSelectedInfo {
  week?: string
  season?: number
  matchupPlayer?: string
  matchupSelected?: string
  matchupSelectedPosition?: number
}

export interface CancelSurveyAnsweredInfo {
  subscriptionPlan?: string
  surveyAnswer?: string
}

export interface SubscriptionCanceledInfo {
  price?: number
  num_renewals?: number
  subscription_plan?: string
  subscription_status?: SubscriptionStatus
  created_timestamp?: number
}

export interface SubscribeHeroImageViewedInfo {
  variant: string
}

export interface SubscribeExperimentViewedInfo {
  variant: string
}

export interface SubscriptionEmailOptedInfo extends Pick<CtaInfo, 'experience' | 'category'> {
  optIn: boolean
}

export interface PlayerProfileInfo {
  player_id: number
  player_name: string
  teamAbv: string
  position: FootballPlayer['position']
  week: string
  season: number
  inSeason: boolean
  screenName: string
  leagueName: string
  location?: PlayerProfileLocation | PlayerProfileLinkNames
  anchors?: PlayerProfileWidgetNames
  linkName?: PlayerProfileLinkNames
  statName?: string
  pastPerformanceToggle?: string
  pastPerformanceStat?: PerformanceMetricStat
  helpToggleOn?: boolean
  similarPlayerId?: number
  similarPlayerName?: string
  similarPlayerPosition?: FootballPlayer['position']
  similarPlayerTeamAbv?: string
  statYear?: string
  statSeasonType?: WeekGroupNfl | WeekGroupNcaa
  variant?: string
  feedback?: string
  pffUserId?: string
  skipNextJsAuth?: boolean
  gradesByGameFilter?: string
  gradesByGameFilterValue?: string
}

export interface PlayerProfileCtaInfo {
  player_id: number
  player_name: string
  teamAbv: string
  position: FootballPlayer['position']
  week: string
  season: number
  inSeason: boolean
  screenName: string
  leagueName: string
  location: PlayerProfileLocation
  pffUserId?: string
  skipNextJsAuth?: boolean
  ctaLocation: string | PlayerProfileLocation
  ctaName: string
}

export interface PlayerPropElementInfo {
  ctaLocation: string
  ctaName: string
  screenName: string
  tabName: string
}

export interface FieldInfo {
  fieldName: string
  location: AccountPages
  skipNextJsAuth?: boolean
}

export interface KeyInsightsFilterAppliedInfo {
  screenName: string
  tabName: string
  filterCategory: string
}

export interface SearchTermInfo {
  screenName: string
  tabName?: string
  searchTerm: string
}

export interface SecondaryFiltersCtaInfo {
  screenName: ScreenName
  tabName: string
  ctaName: string
}

export interface PlayerPropCtaInfo {
  screenName: string
  tabName: string
  ctaName: string
  position?: string
  ctaLocation?: string
}

export interface IdmeVerifiedInfo {
  discount: string
  experience: SubscriptionLocation | AccountPages
}

export interface SortByDropDownInfo {
  screenName: string
  tabName: string
  sortBy: string
}

export interface PlayerPropScreenLoad {
  screenName: string
  tabName: string
  experience: string
}

export type AnalyticsEventProperties = AppInfo &
  DeviceInfo &
  AppOpenedInfo &
  ArticleReadInfo &
  ArticleBookmarkInfo &
  BetCartAnalysisInfo &
  BetSelectedInfo &
  Omit<CtaInfo, 'experience'> &
  ElementInfo &
  EventInfo &
  FilterAppliedInfo &
  GameCardAnalysisInfo &
  Omit<ArticleInfo, 'experience'> &
  Omit<FantasyAnalyticsEventProperties, 'experience'> &
  Omit<SubscriptionPlanSelectedAnalysisInfo, 'isTrialIncluded' | 'experience'> &
  Omit<SubscriptionPurchasedAnalysisInfo, 'isTrialIncluded'> &
  ScreenInfo &
  ScrollToTopButtonAnalysisInfo &
  WebViewInfo &
  PlayerProfileAppInfo &
  ConnectAnalyticsEventProperties &
  GameSimulatorAnalyticsEventProperties &
  WelcomeModalAnalysisInfo &
  LogInInfo &
  AccountCreatedInfo &
  SurveyAnsweredInfo &
  FeedBestBetsInfo &
  FeedEdgeInfo &
  UserInfo & {
    isTrialIncluded: boolean
    experience:
      | FantasyExperience
      | SubscriptionLocation
      | ArticleExperience
      | FantasyLeagueSyncExperience
      | PushNotificationExperienceType
  } & MDSDraftCardInfo &
  PlayerGradeAnalysisInfo &
  TransactionCanceledInfo &
  TransactionFailedInfo &
  GlobalErrorInfo &
  LeagueSyncErrorInfo &
  UpsellModalViewInfo &
  CardSwipedInfo &
  LeagueInfoForAnalytics &
  FantasyToolsElementInfo &
  SPCMonthlyPlanVariantInfo &
  StartSitOptimizerToggleInfo &
  Omit<LeagueSyncFilterOpenedInfo, 'experience'> &
  Omit<LeagueSyncFilterSelectedInfo, 'experience'> &
  MatchupPillSelectedInfo &
  CancelSurveyAnsweredInfo &
  SubscriptionCanceledInfo &
  SubscribeFlowConversionInfo &
  SearchTermInfo &
  KeyInsightsFilterAppliedInfo &
  PlayerPropCtaInfo &
  IdmeVerifiedInfo &
  SortByDropDownInfo &
  PlayerPropScreenLoad &
  SubscriptionEmailOptedInfo

export type ScreenLoadEvent =
  | ScreenInfo
  | WebViewInfo
  | ConnectScreenInfo
  | ConnectUserGroupInfo
  | MyLeaguesViewInfo
  | PlayerProfileAppInfo
  | PlayerPropScreenLoad
export type ArticleShareStartedEvent = ArticleInfo | SharedWebViewInfo
export type ArticleSharedEvent = ArticleInfo | SharedWebViewInfo
export type ArticleOpenedEvent = ArticleInfo
export type ArticleReadEvent = ArticleReadInfo
export type ArticleBookmarkEvent = ArticleBookmarkInfo
export type ElementClickedEvent =
  | ElementInfo
  | FeedEdgeEvent
  | FantasyToolsElementInfo
  | PlayerProfileInfo
  | PlayerPropElementInfo
export type FilterAppliedEvent = FilterAppliedInfo | KeyInsightsFilterAppliedInfo
export type CtaClickedEvent = CtaInfo | FantasyCtaInfo | PlayerProfileCtaInfo | PlayerPropCtaInfo
export type BetSelectedEvent = BetSelectedInfo
export type GameCardAnalysisEvent = GameCardAnalysisInfo
export type BetCartAnalysisEvent = BetCartAnalysisInfo
export type WelcomeModalAnalysisEvent = WelcomeModalAnalysisInfo
export type ScrollToTopButtonAnalysisEvent = ScrollToTopButtonAnalysisInfo
export type SubscriptionPurchasedAnalysisEvent = SubscriptionPurchasedAnalysisInfo
export type SubscriptionPlanSelectedAnalysisEvent = SubscriptionPlanSelectedAnalysisInfo
export type LeagueAddedEvent = LeagueInfoForAnalytics
export type LeagueRemovedEvent = LeagueInfoForAnalytics
export type LoggedInEvent = LogInInfo
export type AccountCreatedEvent = AccountCreatedInfo
export type SurveyAnsweredEvent = SurveyAnsweredInfo
export type ExperimentViewedEvent = FeedBestBetsInfo | ExperimentViewedInfo
export type FeedEdgeEvent = FeedEdgeInfo
export type MDSDraftCardEvent = MDSDraftCardInfo
export type PlayerGradeAnalysisEvent = PlayerGradeAnalysisInfo
export type SubscribeFlowConversionEvent = SubscribeFlowConversionInfo
export type TransactionCanceledEvent = TransactionCanceledInfo
export type TransactionFailedEvent = TransactionFailedInfo
export type GlobalErrorEvent = GlobalErrorInfo
export type LeagueSyncErrorEvent = LeagueSyncErrorInfo
export type UpsellModalViewedEvent = UpsellModalViewInfo
export type CardSwipedEvent = CardSwipedInfo
export type SPCMonthlyPlanVariantEvent = SPCMonthlyPlanVariantInfo
export type StartSitOptimizerToggleEvent = StartSitOptimizerToggleInfo
export type LeagueSyncFilterOpenedEvent = LeagueSyncFilterOpenedInfo
export type LeagueSyncFilterSelectedEvent = LeagueSyncFilterSelectedInfo
export type MatchupPillSelectedEvent = MatchupPillSelectedInfo
export type CancelSurveyAnsweredEvent = CancelSurveyAnsweredInfo
export type SubscriptionCanceledEvent = SubscriptionCanceledInfo
export type SubscribeHeroImageViewedEvent = SubscribeHeroImageViewedInfo
export type SubscribeExperimentViewedEvent = SubscribeExperimentViewedInfo
export type IdmeVerifiedEvent = IdmeVerifiedInfo
export type DropDownEvent = SortByDropDownInfo

/**
 * *******************************
 * >>>>> ADD NEW EVENTS HERE <<<<<
 * *******************************
 *
 * - eventName should be a unique string (cannot be named 'click', 'change', or 'submit')
 * - eventProperties should be typed
 *
 * NOTE - Also need to add all Event properties to ./heap-types.ts and ./map-heap-events.ts (properties will NOT show up in heap unless they are added in those places)
 */
export type AnalyticsEventArgs =
  | [eventName: 'Application Installed']
  | [eventName: 'Application Opened', eventProperties: AppOpenedInfo]
  | [eventName: 'Application Backgrounded']
  | [eventName: 'Account Created']
  | [eventName: 'Screen Load', eventProperties: ScreenLoadEvent]
  | [eventName: 'Article Share Started', eventProperties: ArticleShareStartedEvent]
  | [eventName: 'MDS Shared', eventProperties: ArticleShareStartedEvent]
  | [eventName: 'MDS Share Started', eventProperties: ArticleShareStartedEvent]
  | [eventName: 'Article Shared', eventProperties: ArticleSharedEvent]
  | [eventName: 'Article Opened', eventProperties: ArticleOpenedEvent]
  | [eventName: 'Article Read', eventProperties: ArticleReadEvent]
  | [eventName: 'Article Bookmarked', eventProperties: ArticleBookmarkEvent]
  | [eventName: 'Bookmark Removed', eventProperties: ArticleBookmarkEvent]
  | [eventName: 'Element Clicked', eventProperties: ElementClickedEvent]
  | [eventName: 'CTA Clicked', eventProperties: CtaClickedEvent]
  | [eventName: 'Filter Applied', eventProperties: FilterAppliedEvent]
  | [eventName: 'Bet Selected', eventProperties: BetSelectedEvent]
  | [eventName: 'Bet Deselected', eventProperties: BetSelectedEvent]
  | [eventName: 'Game Card Analysis Expanded', eventProperties: GameCardAnalysisEvent]
  | [eventName: 'Game Card Analysis Collapsed', eventProperties: GameCardAnalysisEvent]
  | [eventName: 'Game Card Analysis Viewed', eventProperties: GameCardAnalysisEvent]
  | [eventName: 'Game Card More Analysis Modal Opened', eventProperties: GameCardAnalysisEvent]
  | [eventName: 'Game Card More Analysis Modal Closed', eventProperties: GameCardAnalysisEvent]
  | [EventName: 'Games View Loaded']
  | [EventName: 'Player Props View Loaded']
  | [EventName: 'Best Bets View Loaded']
  | [EventName: 'Key Insights View Loaded']
  | [EventName: 'Buy Insight Viewed', eventProperties: BetCartAnalysisEvent]
  | [EventName: 'Buy Insight Clicked', eventProperties: BetCartAnalysisEvent]
  | [EventName: 'Sell Insight Viewed', eventProperties: BetCartAnalysisEvent]
  | [EventName: 'Sell Insight Clicked', eventProperties: BetCartAnalysisEvent]
  | FantasyAnalyticsEventArgs
  | [eventName: 'Bet Cart Opened', eventProperties: BetCartAnalysisEvent]
  | [eventName: 'Bet Cart Closed', eventProperties: BetCartAnalysisEvent]
  | [eventName: 'Bet Cart Place Bet', eventProperties: BetCartAnalysisEvent]
  | [eventName: 'Bet Cart Clear All', eventProperties: BetCartAnalysisEvent]
  | [eventName: 'Bet Cart Bet Removed', eventProperties: BetSelectedEvent]
  | [eventName: 'Welcome Modal Screen Viewed', eventProperties: WelcomeModalAnalysisEvent]
  | [eventName: 'Welcome Modal Closed', eventProperties: WelcomeModalAnalysisEvent]
  | [eventName: 'Welcome Modal Get Started Clicked', eventProperties: WelcomeModalAnalysisEvent]
  | [eventName: 'ScrollToTopButton Clicked', eventProperties: ScrollToTopButtonAnalysisEvent]
  | [eventName: 'Subscription Purchased', eventProperties: SubscriptionPurchasedAnalysisEvent]
  | [eventName: 'Subscription Plan Selected', eventProperties: SubscriptionPlanSelectedAnalysisEvent]
  | [eventName: 'Logged In', eventProperties: LoggedInEvent]
  | [eventName: 'Account Created', eventProperties: AccountCreatedEvent]
  | [eventName: 'Survey Answered', eventProperties: SurveyAnsweredEvent]
  | [eventName: 'Game Selected', eventProperties: GameSelectedEvent]
  | [eventName: 'Experiment Viewed', eventProperties: ExperimentViewedEvent]
  | ConnectAnalyticsEventArgs
  | GameSimulatorAnalyticsEventArgs
  | [eventName: 'Card Clicked', eventProperties: MDSDraftCardEvent]
  | [eventName: 'Past Defense Tab Clicked', eventProperties: PlayerGradeAnalysisEvent]
  | [eventName: 'Swiped to Last 5 Games View', eventProperties: PlayerGradeAnalysisEvent]
  | [eventName: 'Subscribe Flow Conversion', eventProperties: SubscribeFlowConversionEvent]
  | [eventName: 'Transaction Canceled', eventProperties: TransactionCanceledEvent]
  | [eventName: 'Transaction Failed', eventProperties: TransactionFailedEvent]
  | [eventName: 'App-wide Error', eventProperties: GlobalErrorEvent]
  | [eventName: 'League Sync Error', eventProperties: LeagueSyncErrorEvent]
  | [eventName: 'Remove League Error', eventProperties: LeagueSyncErrorEvent]
  | [eventName: 'Upsell Modal Viewed', eventProperties: UpsellModalViewedEvent]
  | [eventName: 'Card Swiped', eventProperties: CardSwipedEvent]
  | [eventName: 'League Added', eventProperties: LeagueAddedEvent]
  | [eventName: 'League Removed', eventProperties: LeagueRemovedEvent]
  | [eventName: 'SPC Default Monthly Plan']
  | [eventName: 'SPC Default Annual Plan']
  | [eventName: 'SPC Monthly Plan Variant', eventProperties: SPCMonthlyPlanVariantEvent]
  | [eventName: 'Start Sit Optimizer Toggle', eventProperties: StartSitOptimizerToggleEvent]
  | [eventName: 'League Sync Filter Opened', eventProperties: LeagueSyncFilterOpenedEvent]
  | [eventName: 'League Sync Filter Selected', eventProperties: LeagueSyncFilterSelectedEvent]
  | [eventName: 'Turned On Push Notifications', eventProperties: PushNotificationEvent]
  | [eventName: 'Turned Off Push Notifications', eventProperties: PushNotificationEvent]
  | [eventName: 'Matchup Selected', eventProperties: MatchupPillSelectedEvent]
  | [eventName: 'Cancel Survey Answered', eventProperties: CancelSurveyAnsweredEvent]
  | [eventName: 'Subscription Canceled', eventProperties: SubscriptionCanceledEvent]
  | [eventName: 'Subscribe Hero Image Viewed', eventProperties: SubscribeHeroImageViewedEvent]
  | [eventName: 'Subscribe Experiment Viewed', eventProperties: SubscribeExperimentViewedEvent]
  | [eventName: 'Field Changed', eventProperties: FieldInfo]
  | [eventName: EventNames.TAB_CLICKED, eventProperties: BettingTabClickedEventProperties]
  | [eventName: 'Search Usage', eventProperties: SearchTermInfo]
  | [eventName: 'ID.me Verified', eventProperties: IdmeVerifiedEvent]
  | [eventName: 'Dropdown Clicked', eventProperties: DropDownEvent]
  | [eventName: 'Checkbox Selected', eventProperties: SecondaryFiltersCtaInfo]
  | [eventName: 'Slider Adjusted', eventProperties: SecondaryFiltersCtaInfo]
  | [eventName: 'Email Opt-In', eventProperties: SubscriptionEmailOptedInfo]

export interface Analytics {
  initializeAnalytics: (...args: any[]) => void
  sendAnalyticsEvent: (...args: AnalyticsEventArgs) => Promise<void>
  addAnalyticsUser: (user: User) => void
  removeAnalyticsUser: () => void
}

export interface BettingAnalytics {
  betTrackingFn: (...args: any[]) => void
  betCartTrackingFn: (...args: any[]) => void
}

export interface AnalyticsModule {
  useAnalytics: () => Analytics
  withNavigationAnalytics: (...args: any[]) => any
  // TODO move this to a betting feature lib
  useBettingAnalytics: () => BettingAnalytics
}

export type AnalyticsNavigationContainerProps = NavigationContainerProps & {
  onReady?: () => void

  linking?: LinkingOptions<any>
}

export interface AnalyticsEventProps {
  subscriptionType: SubscriptionType
  subscriptionStatus: SubscriptionStatus
}

export interface PushNotificationEvent {
  experience: PushNotificationExperienceType
}
